import { useCallback, useContext, useMemo, useState } from "react";
import {
    Modal,
    Typography,
    Button,
    Row,
    Col,
    Card,
    Space,
    Tag,
    Input,
    Tooltip,
    Segmented,
} from "antd";
import {
    CalendarOutlined,
    CheckCircleOutlined,
    CopyOutlined,
    DollarCircleOutlined,
    LinkOutlined,
    SearchOutlined,
    ShopOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { convertMoedaBRDoJSNovo, formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { normalizeLink, verifyLinkIsValid } from "../../../../utils/Functions";
import { IDuplicatedInvoices, IDuplicatedInvoicesItem } from "../../../../interfaces";
import { formatCpfCnpj } from "../../../../utils/Masks";
import { UpdateInvoiceStatusModal } from "../../../../components/UpdateInvoiceStatusModal";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Paragraph } = Typography;

export interface ITableData extends IDuplicatedInvoices {
    rowKey: string;
}

interface IProps {
    open: boolean;
    onClose: () => void;
    invoice: ITableData | undefined;
    onRefresh: () => Promise<void>;
}

export default function ModalInvoiceDetails({
    open,
    onClose,
    invoice,
    onRefresh,
}: IProps) {
    const { theme } = useContext<any>(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const [invoiceSelected, setInvoiceSelected] = useState<IDuplicatedInvoicesItem>();
    const [modalEditOpen, setModalEditOpen] = useState<boolean>(false);

    const [filterType, setFilterType] = useState<string>("ALL");
    const [filterSearch, setFilterSearch] = useState<string>("");

    const handleSelectedInvoice = useCallback((item: IDuplicatedInvoicesItem) => {
        setInvoiceSelected(item);
        setModalEditOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalEditOpen(false);
        setInvoiceSelected(undefined);
        onRefresh();
    }, [onRefresh]);

    const handleGoToUser = () => {
        if (invoice) {
            navigate(`/usuarios/aplicativo/visualizar/${invoice.appUser.id}`);
        }
    };

    const handleGoToInvoice = (id: number) => {
        navigate(`/nota/detalhes/${id}`);
    };

    const cardBg = isDark ? "#1f1f1f" : "#ffffff";
    const cardBorder = isDark ? "#303030" : "#e2e8f0";

    const getTypeColor = (type?: string) => {
        if (type === "NFC") return { color: "purple", primary: "#722ed1", bg: isDark ? "rgba(114, 46, 209, 0.12)" : "#f9f0ff", border: isDark ? "#531dab" : "#d3adf7" };
        if (type === "NFE") return { color: "cyan", primary: "#08979c", bg: isDark ? "rgba(8, 151, 156, 0.12)" : "#e6fffb", border: isDark ? "#006d75" : "#87e8de" };
        return { color: "blue", primary: "#1677ff", bg: isDark ? "rgba(22, 119, 255, 0.12)" : "#e6f4ff", border: isDark ? "#114598" : "#91caff" };
    };

    const typeCounts = useMemo(() => {
        if (!invoice?.invoices) return {};
        const counts: Record<string, number> = {};
        invoice.invoices.forEach((inv) => {
            const t = inv.type || "NFS";
            counts[t] = (counts[t] || 0) + 1;
        });
        return counts;
    }, [invoice?.invoices]);

    const typeOptions = useMemo(() => {
        if (!invoice?.invoices) return [];
        const options = [
            {
                label: `Todos (${invoice.invoices.length})`,
                value: "ALL",
            },
        ];

        const uniqueTypes = Array.from(new Set(invoice.invoices.map((inv) => inv.type || "NFS")));
        uniqueTypes.forEach((t) => {
            options.push({
                label: `${t} (${typeCounts[t] || 0})`,
                value: t,
            });
        });

        return options;
    }, [invoice?.invoices, typeCounts]);

    const filteredInvoices = useMemo(() => {
        if (!invoice?.invoices) return [];
        return invoice.invoices.filter((item) => {
            if (filterType !== "ALL" && (item.type || "NFS") !== filterType) {
                return false;
            }
            if (filterSearch) {
                const q = filterSearch.toLowerCase().trim();
                const idMatch = String(item.id).includes(q);
                const compMatch = item.company?.toLowerCase().includes(q);
                const keyMatch = item.key ? String(item.key).toLowerCase().includes(q) : false;
                const cpfMatch = item.cpfCnpj ? String(item.cpfCnpj).includes(q) : false;
                return idMatch || compMatch || keyMatch || cpfMatch;
            }
            return true;
        });
    }, [invoice?.invoices, filterType, filterSearch]);

    if (!invoice) return null;

    return (
        <Modal
            width={1120}
            open={open}
            centered
            destroyOnClose
            title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
                    <Space size={10} align="center">
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: isDark ? "rgba(250, 84, 28, 0.15)" : "#fff2e8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CopyOutlined style={{ color: "#fa541c", fontSize: 17 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                                Comparador de Notas Duplicadas
                            </div>
                            <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 1 }}>
                                Filtre por tipo, analise as notas e valide a opção correta
                            </div>
                        </div>
                    </Space>
                </div>
            }
            footer={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>
                        Exibindo <strong>{filteredInvoices.length}</strong> de <strong>{invoice.invoices.length}</strong> notas
                    </span>
                    <Button type="primary" onClick={onClose} style={{ minWidth: 90 }}>
                        Fechar
                    </Button>
                </div>
            }
            onCancel={onClose}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "4px 0" }}>
                <div
                    style={{
                        background: isDark ? "#141414" : "#f8fafc",
                        border: `1px solid ${cardBorder}`,
                        borderRadius: 8,
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 10,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <UserOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                        <span style={{ fontWeight: 700, fontSize: 13.5, color: isDark ? "#f0f0f0" : "#0f172a" }}>
                            #{invoice.appUser.id} {invoice.appUser.name}
                        </span>
                        <Button
                            size="small"
                            type="link"
                            onClick={handleGoToUser}
                            style={{ padding: "0 4px", fontSize: 12, fontWeight: 500 }}
                        >
                            Ver Perfil
                        </Button>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{ fontSize: 12 }}>
                            <span style={{ color: isDark ? "#8c8c8c" : "#64748b" }}>Valor do Grupo: </span>
                            <strong style={{ color: isDark ? "#52c41a" : "#237804", fontSize: 14 }}>
                                R$ {convertMoedaBRDoJSNovo(invoice.value)}
                            </strong>
                        </div>

                        <Tag color="#1677ff" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, margin: 0 }}>
                            {invoice.total} {invoice.total === 1 ? "nota cadastrada" : "notas cadastradas"}
                        </Tag>
                    </div>
                </div>
                <div
                    style={{
                        background: isDark ? "#141414" : "#f1f5f9",
                        border: `1px solid ${cardBorder}`,
                        borderRadius: 8,
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 10,
                    }}
                >

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d1d5db" : "#475569" }}>
                            Tipo:
                        </span>
                        <Segmented
                            size="small"
                            value={filterType}
                            onChange={(val) => setFilterType(String(val))}
                            options={typeOptions}
                        />
                    </div>
                    <Input
                        size="small"
                        placeholder="Buscar nota, empresa, chave..."
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        allowClear
                        style={{ width: 240, borderRadius: 6 }}
                    />
                </div>

                <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4, marginTop: 4 }}>
                    {filteredInvoices.length === 0 ? (
                        <div style={{ padding: "30px 0", textAlign: "center", color: isDark ? "#8c8c8c" : "#94a3b8" }}>
                            Nenhuma nota encontrada com os filtros selecionados.
                        </div>
                    ) : (
                        <Row gutter={[12, 12]}>
                            {filteredInvoices.map((item) => {
                                const typeInfo = getTypeColor(item.type);
                                const rawQrLink = item.link ? normalizeLink(item.link).toString() : null;
                                const itemVal = item.value != null ? Number(item.value) : invoice.value;

                                return (
                                    <Col
                                        key={item.id}
                                        xs={24}
                                        md={filteredInvoices.length === 1 ? 24 : 12}
                                        lg={filteredInvoices.length <= 2 ? 12 : 8}
                                    >
                                        <Card
                                            size="small"
                                            style={{
                                                background: cardBg,
                                                border: `1.5px solid ${typeInfo.border}`,
                                                borderRadius: 10,
                                                overflow: "hidden",
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                            bodyStyle={{
                                                padding: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                                height: "100%",
                                                flex: 1,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    background: typeInfo.bg,
                                                    padding: "8px 12px",
                                                    borderBottom: `1px solid ${typeInfo.border}`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                <Space size={6} align="center">
                                                    <Tag
                                                        color={typeInfo.color}
                                                        style={{
                                                            borderRadius: 4,
                                                            fontWeight: 700,
                                                            fontSize: 12,
                                                            padding: "1px 6px",
                                                            margin: 0,
                                                        }}
                                                    >
                                                        {item.type || "NFS"}
                                                    </Tag>
                                                    <Tooltip title="Ver detalhes completos da nota">
                                                        <Button
                                                            type="link"
                                                            onClick={() => handleGoToInvoice(item.id)}
                                                            style={{
                                                                padding: 0,
                                                                fontWeight: 700,
                                                                fontSize: 13,
                                                                color: isDark ? "#93c5fd" : "#1d4ed8",
                                                            }}
                                                        >
                                                            #{item.id}
                                                        </Button>
                                                    </Tooltip>
                                                </Space>

                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<LinkOutlined />}
                                                    onClick={() => handleGoToInvoice(item.id)}
                                                    style={{
                                                        fontSize: 11,
                                                        padding: 0,
                                                        color: isDark ? "#94a3b8" : "#64748b",
                                                        height: "auto",
                                                    }}
                                                >
                                                    Ver Nota
                                                </Button>
                                            </div>

                                            <div
                                                style={{
                                                    padding: "10px 12px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "space-between",
                                                    flex: 1,
                                                    gap: 10,
                                                }}
                                            >
                                                <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: isDark ? "#141414" : "#f8fafc", padding: "6px 8px", borderRadius: 6 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                            <DollarCircleOutlined style={{ color: "#52c41a" }} />
                                                            <span style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#64748b" }}>Valor:</span>
                                                            <strong style={{ color: isDark ? "#52c41a" : "#237804", fontSize: 13 }}>
                                                                R$ {convertMoedaBRDoJSNovo(itemVal)}
                                                            </strong>
                                                        </div>

                                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                            <CalendarOutlined style={{ color: "#722ed1", fontSize: 11 }} />
                                                            <span style={{ fontSize: 11, color: isDark ? "#d1d5db" : "#475569" }}>
                                                                {item.dateIssue ? formatDateStringToDateAndTime(String(item.dateIssue), true) : "—"}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div style={{ display: "flex", flexDirection: "column", background: isDark ? "#141414" : "#f8fafc", padding: "6px 8px", borderRadius: 6 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                            <ShopOutlined style={{ color: "#1677ff", fontSize: 12 }} />
                                                            <span style={{ fontWeight: 600, fontSize: 12, color: isDark ? "#f0f0f0" : "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {item.company || "—"}
                                                            </span>
                                                        </div>
                                                        {item.cpfCnpj && (
                                                            <span style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#94a3b8", marginLeft: 16 }}>
                                                                CNPJ/CPF: {formatCpfCnpj(item.cpfCnpj)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {item.key ? (
                                                        <div style={{ background: isDark ? "#141414" : "#f8fafc", padding: "4px 8px", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                            <span style={{ fontSize: 10, color: isDark ? "#8c8c8c" : "#94a3b8" }}>Chave:</span>
                                                            <Paragraph
                                                                copyable={{ text: String(item.key), tooltips: ["Copiar", "Copiado!"] }}
                                                                style={{
                                                                    margin: 0,
                                                                    fontFamily: "monospace",
                                                                    fontSize: 10.5,
                                                                    fontWeight: 600,
                                                                    color: isDark ? "#60a5fa" : "#1d4ed8",
                                                                }}
                                                            >
                                                                {String(item.key).slice(0, 10)}...{String(item.key).slice(-6)}
                                                            </Paragraph>
                                                        </div>
                                                    ) : null}
                                                    {item.link && verifyLinkIsValid({ link: item.link }) && rawQrLink && (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                            <LinkOutlined style={{ color: "#1677ff", fontSize: 11 }} />
                                                            <a
                                                                href={rawQrLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ fontSize: 11, color: "#1677ff", fontWeight: 500 }}
                                                            >
                                                                Abrir Link da Nota
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                                <Button
                                                    type="primary"
                                                    icon={<CheckCircleOutlined />}
                                                    block
                                                    size="small"
                                                    style={{
                                                        backgroundColor: "#f59e0b",
                                                        borderColor: "#f59e0b",
                                                        fontWeight: 600,
                                                        borderRadius: 6,
                                                        marginTop: 4,
                                                        height: 30,
                                                    }}
                                                    loading={invoiceSelected?.id === item.id && modalEditOpen}
                                                    onClick={() => handleSelectedInvoice(item)}
                                                >
                                                    Validar / Alterar Status
                                                </Button>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </div>
            </div>

            {invoiceSelected && (
                <UpdateInvoiceStatusModal
                    invoice={invoiceSelected}
                    open={modalEditOpen}
                    onClose={handleCloseModal}
                    onSuccess={handleCloseModal}
                />
            )}
        </Modal>
    );
}